<script lang="ts">
	import { Img } from "flowbite-svelte";
	import { getContext } from "svelte";
	import { ConversionType } from "#gen/bundles/v1/bundle_pb.js";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import IFrameVideoPlayer from "#lib/components/IFrameVideoPlayer.svelte";
	import VimeoPlayer from "#lib/components/VimeoPlayer.svelte";
	import WistiaPlayer from "#lib/components/WistiaPlayer.svelte";
	import YoutubePlayer from "#lib/components/YoutubePlayer.svelte";
	import type { ComponentProps } from "#lib/components/page/ComponentProps.js";
	import { isFeatureEnabled } from "#lib/featureFlags.js";
	import type { JwtPayload } from "#lib/jwt.js";
	import { Logger } from "#lib/logging.js";
	import { booleanValue, stringValue, structValue } from "#lib/struct.js";
	import type { Client } from "@optimizely/optimizely-sdk";
	import type { Writable } from "svelte/store";

	type VideoProvider = "youtube" | "vimeo" | "wistia" | "synthesia" | "kaltura" | undefined;

	let { id, style, page, mode, readOnly = false }: ComponentProps = $props();
	const execute: (conversionType: ConversionType, conversionComponent: string, sourceAnchor?: string) => Promise<boolean> = getContext("execute");
	const flags: Client = getContext("flags");
	const claims: Writable<JwtPayload> = getContext("claims");

	const kalturaEnabled = $derived(isFeatureEnabled(flags, "kaltura_embedded_videos", $claims));
	let config = $derived(structValue($page.config, id));
	let src = $derived(stringValue(config, "src") ?? "");
	let autoplay = $derived(mode === Mode.EDITING ? false : booleanValue(config, "autoplay"));
	let provider = $derived(getVideoProvider(src));

	let invalid = $state(false);

	function extractYoutubeVideoId(url?: string): string {
		if (!url) {
			return "";
		}
		try {
			const videoUrl = new URL(url);
			const vParam = videoUrl.searchParams.get("v");
			if (vParam) {
				return vParam;
			}
			if (videoUrl.hostname === "youtu.be") {
				const pathSegments = videoUrl.pathname.split("/").filter(Boolean);
				if (pathSegments.length > 0) {
					return pathSegments[0];
				}
			}
			if (videoUrl.pathname.startsWith("/shorts/")) {
				return videoUrl.pathname.split("/")[2] || "";
			}
			if (videoUrl.pathname.startsWith("/embed/")) {
				return videoUrl.pathname.split("/")[2] || "";
			}
			return "";
		} catch (err) {
			Logger.error("Invalid YouTube URL", { url, err });
			return "";
		}
	}

	function getVideoProvider(url: string): VideoProvider | undefined {
		if (url.includes("youtube.com") || url.includes("youtu.be")) return "youtube";
		if (url.includes("vimeo.com")) return "vimeo";
		if (url.includes("wistia.com")) return "wistia";
		if (url.includes("synthesia.io")) return "synthesia";
		if (url.includes("kaltura.com") && kalturaEnabled) return "kaltura";
		return undefined;
	}

	function extractKalturaId(url: string): string {
		const partnerId = url.match(/(?:\/p\/|partner_id[/=])(\d+)/)?.[1];
		const uiconfId = url.match(/uiconf_id[/=](\d+)/i)?.[1];
		const entryId = url.match(/entry_?id[/=]([\w-]+)/i)?.[1];
		if (partnerId && uiconfId && entryId) {
			return `${partnerId}|${uiconfId}|${entryId}`;
		}
		if (/\.mediaspace\.kaltura\.com\/media\/[\w-]+/i.test(url) || /\/embed\/secure\/iframe\/entryId\/[\w-]+\/uiConfId\/\d+/i.test(url)) {
			return url;
		}
		return "";
	}

	function extractVideoId(provider: VideoProvider, url?: string): string {
		if (!url) return "";
		if (provider === "vimeo") {
			try {
				const match = url.match(/vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/|album\/\d+\/video\/)?(\d+)(?:\/([a-z0-9]+))?/i);
				if (!match) return "";
				const id = match[1];
				const hash = match[2];
				return hash ? `${id}?h=${hash}` : id;
			} catch {
				return "";
			}
		} else if (provider === "wistia") {
			const match = url.match(/(?:medias|iframe)\/([a-zA-Z0-9]+)/);
			return match?.[1] ?? "";
		} else if (provider === "youtube") {
			return extractYoutubeVideoId(url);
		} else if (provider === "synthesia") {
			return url.split("/").pop() ?? "";
		} else if (provider === "kaltura" && kalturaEnabled) {
			return extractKalturaId(url);
		}
		return "";
	}

	async function onVideoEnd() {
		try {
			await execute(ConversionType.NONE, id);
		} catch (_: unknown) {
			// TODO: Use the error here to show a helpful message to the user
		}
	}
</script>

<div class={readOnly ? "pointer-events-none w-full" : ""}>
	{#if src}
		{#if invalid || !provider || !provider.length}
			<Img {style} src="/carousel-1.svg" alt="Invalid video" />
		{:else if provider === "youtube"}
			<YoutubePlayer {style} {...config} {autoplay} src={extractYoutubeVideoId(stringValue(config, "src"))} on:error={() => (invalid = true)} on:ended={onVideoEnd} />
		{:else if provider === "vimeo"}
			<VimeoPlayer {style} {...config} {autoplay} src={extractVideoId(provider, src)} on:ended={onVideoEnd} />
		{:else if provider === "wistia"}
			<WistiaPlayer {style} {...config} {autoplay} src={extractVideoId(provider, src)} on:ended={onVideoEnd} />
		{:else if provider === "synthesia" || (provider === "kaltura" && kalturaEnabled)}
			<IFrameVideoPlayer {style} {...config} {autoplay} src={extractVideoId(provider, src)} {provider} />
		{/if}
	{/if}
</div>
